import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Globe,
  Flame,
  FileText,
  Footprints,
  HelpCircle,
  ArrowRight,
  Blocks,
  Wind,
  BarChart,
  Calculator,
  ChevronRight,
} from "lucide-react";
import c2cLogo from "../assets/images/logo/c2c-logo-removebg-preview.png";
import { useCourseProgress } from "../contexts/CourseProgressContext";

const Home = () => {
  const [, setScrolled] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);
  const { isModuleCompleted } = useCourseProgress();

  useEffect(() => {
    // Animation entrance timing
    setTimeout(() => {
      setAnimationComplete(true);
    }, 1000);

    // Scroll effect
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const courseModules = [
    {
      title: "Why It Matters",
      description:
        "Understand how climate change affects communities around the world via real stories.",
      icon: Globe,
      id: "why-it-matters",
      link: "/why-it-matters",
    },
    {
      title: "How Climate Engine Works",
      description:
        "Learn the core mechanics of our climate system and how human activities affect it.",
      icon: Wind,
      id: "climate-engine",
      link: "/climate-engine",
    },
    {
      title: "Carbon Emissions 101",
      description:
        "Explore the history of emissions tracking and how to calculate carbon footprints.",
      icon: BarChart,
      id: "carbon-emissions-101",
      link: "/carbon-emissions-101",
    },
    {
      title: "Make It Visible",
      description:
        "See where carbon hides in everyday life and learn practical ways to reduce your impact.",
      icon: Footprints,
      id: "uae-carbon-day",
      link: "/uae-carbon-day",
    },
    {
      title: "Case Studies",
      description:
        "Examine real-world examples of climate disasters and their relationship to emissions.",
      icon: Flame,
      id: "case-studies",
      link: "/case-studies",
    },
    {
      title: "Carbon Footprint 101",
      description:
        "Apply your knowledge to measure and reduce your own personal carbon impact.",
      icon: Calculator,
      id: "carbon-footprint",
      link: "/carbon-footprint",
    },
    {
      title: "Quiz & Mini-Games",
      description:
        "Test your knowledge and practice making climate-friendly decisions.",
      icon: HelpCircle,
      id: "quiz",
      link: "/quiz",
    },
    {
      title: "Action Toolkit",
      description:
        "Discover concrete steps you can take to reduce your carbon footprint.",
      icon: Blocks,
      id: "action-toolkit",
      link: "/action-toolkit",
    },
    {
      title: "Further Learning",
      description:
        "Explore additional resources and success stories to continue your journey.",
      icon: FileText,
      id: "further-learning",
      link: "/further-learning",
    },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section with enhanced visuals */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 text-white pb-24 pt-8">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-96 h-96 rounded-full bg-green-400 opacity-10 -top-20 -left-20 animate-pulse"></div>
          <div
            className="absolute w-64 h-64 rounded-full bg-blue-400 opacity-10 top-1/2 right-20 animate-pulse"
            style={{ animationDelay: "1s" }}
          ></div>
          <div
            className="absolute w-80 h-80 rounded-full bg-teal-400 opacity-10 bottom-0 left-1/3 animate-pulse"
            style={{ animationDelay: "2s" }}
          ></div>

          {/* Grid Pattern */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-primary-900/30">
            <div
              className="absolute inset-0"
              style={{
                backgroundColor: "#1b473e",
                backgroundSize: "30px 30px",
              }}
            ></div>
          </div>
        </div>

        {/* Content Container */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Logo with animation */}
          <div className="flex items-center justify-between">
            <div
              className={`transition-all duration-700 ${
                animationComplete
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 -translate-y-8"
              }`}
            >
              <img
                src={c2cLogo}
                alt="C2C Logo"
                className="w-44 h-auto drop-shadow-lg"
              />
            </div>
          </div>

          {/* Hero Content */}
          <div className="md:w-3/4 lg:w-2/3">
            {/* Heading with animation */}
            <h1
              className={`text-5xl md:text-6xl font-bold mb-8 leading-tight transition-all duration-1000 ${
                animationComplete
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
              style={{ textShadow: "0 2px 10px rgba(0, 0, 0, 0.2)" }}
            >
              <span className="block">Climate change is here.</span>
              <span className="block mt-2 text-edu-orange">
                It's personal
                <span className="text-white">
                  —and we can make a difference.
                </span>
              </span>
            </h1>

            {/* Description with animation */}
            <p
              className={`text-xl md:text-2xl mb-10 text-white text-opacity-90 leading-relaxed transition-all duration-1000 delay-300 ${
                animationComplete
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
            >
              This course will guide you through understanding climate change,
              its impacts, and how you can take meaningful action in your daily
              life.
            </p>

            {/* CTA Button with animation */}
            <div
              className={`transition-all duration-1000 delay-500 ${
                animationComplete
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
            >
              <Link
                to="/why-it-matters"
                className="group inline-flex items-center bg-edu-orange text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-opacity-90 transition-all hover:shadow-lg hover:translate-y-[-2px]"
              >
                Begin Your Journey
                <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>

          {/* Decorative Elements */}
          <div className="absolute right-0 top-1/2 transform -translate-y-1/2 hidden lg:block">
            <div className="relative">
              {/* Earth icon with animation */}
              <div
                className={`absolute top-0 right-20 transition-all duration-1000 delay-700 ${
                  animationComplete
                    ? "opacity-100 scale-100"
                    : "opacity-0 scale-50"
                }`}
              ></div>

              {/* Flame icon with animation */}
              <div
                className={`absolute top-32 right-0 transition-all duration-1000 delay-800 ${
                  animationComplete
                    ? "opacity-100 scale-100"
                    : "opacity-0 scale-50"
                }`}
              ></div>

              {/* Wind icon with animation */}
              <div
                className={`absolute top-72 right-20 transition-all duration-1000 delay-900 ${
                  animationComplete
                    ? "opacity-100 scale-100"
                    : "opacity-0 scale-50"
                }`}
              ></div>
            </div>
          </div>
        </div>

        {/* Curved bottom edge */}
        <div className="absolute -bottom-1 left-0 right-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 60">
            <path
              fill="#f9fafb"
              fillOpacity="1"
              d="M0,32L80,37.3C160,43,320,53,480,48C640,43,800,21,960,16C1120,11,1280,21,1360,26.7L1440,32L1440,60L1360,60C1280,60,1120,60,960,60C800,60,640,60,480,60C320,60,160,60,80,60L0,60Z"
            ></path>
          </svg>
        </div>
      </section>

      {/* Course Overview Section with improved styling */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
              Your Carbon Literacy Journey
            </h2>
            <div className="w-20 h-1 bg-edu-orange mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Complete all modules to become carbon literate and equipped to
              make climate-friendly choices in your daily life.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courseModules.map((module) => {
              const completed = isModuleCompleted(module.id);
              const progress = completed ? 100 : 0;

              return (
                <div
                  key={module.id}
                  className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 overflow-hidden border border-gray-100"
                >
                  <div className="p-6">
                    <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center mb-4">
                      <module.icon className="h-6 w-6 text-primary-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">
                      {module.title}
                    </h3>
                    <p className="text-gray-600 mb-4 min-h-[80px]">
                      {module.description}
                    </p>

                    <div className="mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                        <div
                          className={`${
                            completed ? "bg-green-500" : "bg-primary-500"
                          } h-2 rounded-full`}
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                      <div className="text-xs text-gray-500 mb-4">
                        {completed ? (
                          <span className="text-green-600 font-medium">
                            Completed
                          </span>
                        ) : (
                          `${progress}% Complete`
                        )}
                      </div>
                    </div>

                    <Link
                      to={module.link}
                      className="inline-flex items-center justify-center w-full py-2 px-4 bg-edu-orange hover:bg-edu-orange/90 text-white rounded-lg transition-colors font-medium"
                    >
                      {completed ? "Review" : "Start"} Module
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
